import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Button, message } from 'antd';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchMinimalLicenseAndVehicle, getMinimalVehicles } from '../../store/drivers.store';

import { addFuelRecord /* updateFuelRecord */ } from '../../store/fuel.store';

const FuelSummary = ({ visible, onClose }: { visible: boolean; onClose: () => void }): JSX.Element => {
  const vehicles = useAppSelector(getMinimalVehicles);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [fuelLogId, setFuelLogId] = useState<number | null>(null);

  const [quantity, setQuantity] = useState(0);
  const [unitPrice, setUnitPrice] = useState(0);
  const [fuelCost, setFuelCost] = useState(0);

  useEffect(() => {
    if (visible) {
      dispatch(fetchMinimalLicenseAndVehicle(null));
    }
  }, [visible, dispatch]);

  useEffect(() => {
    const cost = quantity * unitPrice;
    setFuelCost(cost);
    form.setFieldsValue({ fuelCost: cost });
  }, [quantity, unitPrice, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const selectedVehicle = vehicles.find((v) => v.plateNumber === values.vehicleNumber);
      const vehicleId = (selectedVehicle as any)?.vehicle_id;
      if (!vehicleId) {
        message.error('Could not resolve the selected vehicle');
        return;
      }
      const payload = {
        plateNumber: values.vehicleNumber,
        vehicleId,
        fuelEfficiency: (selectedVehicle as any)?.fuelEfficiency ?? 0,
        fuelFillingDate: `${values.fuelFillingDate.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`,
        quantity: values.quantity,
        unitPrice: values.unitPrice,
        odometerReading: values.odometerReading,
        fuelCost,
      };
      dispatch(addFuelRecord(payload));
      onClose();
      form.resetFields();
      setFuelLogId(null);
    } catch (error) {
      message.error('Failed to submit fuel record');
    }
  };

  const handleVehicleChange = (value: string) => {
    const selectedVehicle = vehicles.find((vehicle) => vehicle.plateNumber === value);
    form.setFieldsValue({
      fuelType: selectedVehicle ? selectedVehicle.fuelType : '',
    });
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      title="Add Fuel Record"
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {fuelLogId ? 'Update' : 'Submit'}
        </Button>,
      ]}
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          label="Vehicle Number :"
          name="vehicleNumber"
          rules={[{ required: true, message: 'Please select the vehicle number' }]}
        >
          <Select placeholder="Select Vehicle Number" onChange={handleVehicleChange}>
            {vehicles.map((vehicle) => (
              <Select.Option key={vehicle.plateNumber} value={vehicle.plateNumber}>
                {vehicle.plateNumber}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Fuel Type :" name="fuelType" rules={[{ required: true }]}>
          <Input placeholder="Fuel Type" disabled style={{ color: 'black' }} />
        </Form.Item>
        <Form.Item
          label="Fuel Filling Date :"
          name="fuelFillingDate"
          rules={[{ required: true, message: 'Please select fuel filling date' }]}
        >
          <DatePicker style={{ width: '100%' }} placeholder="Select Date" />
        </Form.Item>
        <Form.Item
          label="Fuel Quantity :"
          name="quantity"
          rules={[{ required: true, message: 'Please enter fuel quantity in Liters' }]}
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            addonAfter="L"
            value={quantity}
            onChange={(value) => setQuantity(Number(value))}
          />
        </Form.Item>
        <Form.Item
          label="Price Per Liter :"
          name="unitPrice"
          rules={[{ required: true, message: 'Please enter price per liter' }]}
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            addonAfter="LKR"
            value={unitPrice}
            onChange={(value) => setUnitPrice(Number(value))}
          />
        </Form.Item>
        <Form.Item
          label="Odometer Reading :"
          name="odometerReading"
          rules={[{ required: true, message: 'Please enter odometer reading' }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} addonAfter="Km" />
        </Form.Item>
        <Form.Item label="Fuel Cost :" name="fuelCost">
          <InputNumber style={{ width: '100%' }} addonAfter="LKR" value={fuelCost} readOnly />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FuelSummary;
